/**
 * MedicalClinicPanel — what a clinic coordinator (or head office) actually
 * needs to see, from data the Command Center payload already carries:
 *
 *   · IMPROVEMENT TOPICS — the clinic's own concern topics, counted over the
 *     same 30 days, labelled from the SAME canonical keys the guest flow writes.
 *   · CONSULT REQUESTS — the queue from the tap card. The first reply claims it.
 *
 * No new panel framework, no new fetch: both arrays arrive on
 * `/api/command-center/overview` for the medical vertical.
 */
import { useState } from 'react';
import { Stethoscope, PhoneCall, Loader2 } from 'lucide-react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';

const fmtWhen = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function MedicalClinicPanel({ data, t }) {
    const topics = data?.improvement_categories || [];
    const [consults, setConsults] = useState(data?.consults || []);
    const [busyId, setBusyId] = useState('');
    const [error, setError] = useState('');

    const claim = async (c) => {
        setBusyId(c.id);
        setError('');
        try {
            const res = await api.post(`/medical/consults/${c.id}/reply`, {
                body: t('commandCenter.medical.consults.defaultReply',
                    'Thank you for your request — the clinic will contact you shortly.'),
            });
            const updated = res.data?.consult;
            setConsults((rows) => rows.map((r) => (r.id === c.id ? { ...r, ...updated } : r)));
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally {
            setBusyId('');
        }
    };

    return (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="cc-band-medical">
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none"
                data-testid="cc-medical-topics">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                    <Stethoscope className="h-4 w-4 text-emerald-500" />
                    {t('commandCenter.medical.topics.title')}
                </h3>
                {topics.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="cc-medical-topics-empty">
                        {t('commandCenter.medical.topics.empty')}
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {topics.map((row) => (
                            <li key={row.category} className="flex items-center justify-between gap-3"
                                data-testid={`cc-medical-topic-${row.category}`}>
                                <span className="truncate text-sm text-slate-700 dark:text-slate-300">
                                    {t(`reviewShield.improvementCategories.${row.category}`, row.category)}
                                </span>
                                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                    {row.count}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none"
                data-testid="cc-medical-consults">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                    <PhoneCall className="h-4 w-4 text-emerald-500" />
                    {t('commandCenter.medical.consults.title')}
                </h3>
                {error && <p className="mb-2 text-xs text-rose-500" data-testid="cc-medical-consults-error">{error}</p>}
                {consults.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="cc-medical-consults-empty">
                        {t('commandCenter.medical.consults.empty')}
                    </p>
                ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {consults.map((c) => (
                            <li key={c.id} className="flex items-center gap-3 py-2"
                                data-testid={`cc-medical-consult-${c.id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                                        {c.patient?.name || '—'}
                                        <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                                            {c.clinic_name}
                                        </span>
                                    </div>
                                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                        {c.patient?.phone || c.patient?.email || '—'} · {fmtWhen(c.created_at)}
                                    </div>
                                </div>
                                {c.assigned_staff_name ? (
                                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                                        data-testid={`cc-medical-consult-claimed-${c.id}`}>
                                        {t('commandCenter.medical.consults.claimedBy', { name: c.assigned_staff_name })}
                                    </span>
                                ) : (
                                    <Button size="sm" variant="ghost" disabled={busyId === c.id}
                                        onClick={() => claim(c)}
                                        data-testid={`cc-medical-consult-claim-${c.id}`}
                                        className="h-7 shrink-0 text-xs text-slate-600 dark:text-slate-300">
                                        {busyId === c.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                        {t('commandCenter.medical.consults.claim')}
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
